/**
 * Date range utilities: week start, expression validation/resolution, legacy coercion, presets.
 */

import { DateRange } from '@angular/material/datepicker';
import {
	DateRangeConfigType,
	type DateRangePreset,
	type DynamicDateRangeConfig,
	type RelativeOffsetDateRangeConfig,
} from '../models/date-range-config.model';
import {
	DATE_RANGE_SINGLE_PRESETS,
	type DateRangeExpression,
	type DateRangeSinglePreset,
	type ResolvedDateRangeExpression,
} from '../models/date-range-expression.model';
import {
	formatDateToISO,
	isValidDateRangeConfig,
	parseISODate,
	resolveDateRangeConfig,
} from './date-range-resolver.util';

export { formatDateToISO, parseISODate } from './date-range-resolver.util';

// =============================================================================
// Week start
// =============================================================================

/** First day of the calendar week. */
export type WeekStart = 'sunday' | 'monday';

/** Account setting strings from Jepto settings. */
export type WeekStartSetting =
	| WeekStart
	| 'Sunday to Saturday'
	| 'Monday to Sunday';

export interface WeekStartOptions {
	weekStart?: WeekStart;
}

export const DEFAULT_WEEK_START: WeekStart = 'monday';

/** Default picker/card trigger display, e.g. `Apr 1 2025` (month token is locale-aware). */
export const DEFAULT_DATE_RANGE_DISPLAY_FORMAT = 'MMM d yyyy';

/**
 * Formats a date for date-range display inputs.
 * Supports `yyyy`, `MMM` (localized short month), `MM`, `dd`, and `d`.
 */
export function formatDateRangeDisplayDate(
	date: Date,
	locale: string,
	format: string = DEFAULT_DATE_RANGE_DISPLAY_FORMAT
): string {
	const day = date.getDate();
	const month = date.getMonth();
	const year = date.getFullYear();
	const monthAbbrev = date.toLocaleString(locale, { month: 'short' });

	return format
		.replace('yyyy', String(year))
		.replace('MMM', monthAbbrev)
		.replace('MM', String(month + 1).padStart(2, '0'))
		.replace('dd', String(day).padStart(2, '0'))
		.replace('d', String(day));
}

/** Normalizes account / input values to a week start day. Throws when missing or unrecognized. */
export function coerceWeekStart(value: WeekStartSetting): WeekStart;
export function coerceWeekStart(
	value: WeekStartSetting | undefined | null
): WeekStart;
export function coerceWeekStart(
	value: WeekStartSetting | undefined | null
): WeekStart {
	if (value === null || value === undefined) {
		throw new Error('weekStart is required');
	}
	const normalized = String(value).trim().toLowerCase();
	if (normalized === '') {
		throw new Error('weekStart is required');
	}
	if (normalized === 'sunday' || normalized === 'sunday to saturday') {
		return 'sunday';
	}
	if (normalized === 'monday' || normalized === 'monday to sunday') {
		return 'monday';
	}
	throw new Error(`Invalid weekStart: ${JSON.stringify(value)}`);
}

/** Resolves week start from expression options. Throws when `weekStart` is missing. */
export function weekStartFromOptions(options?: WeekStartOptions): WeekStart {
	return coerceWeekStart(options?.weekStart);
}

/** JavaScript `Date#getDay()` index for the first column in Material calendars. */
export function getFirstDayOfWeekIndex(
	weekStart: WeekStart = DEFAULT_WEEK_START
): 0 | 1 {
	return weekStart === 'sunday' ? 0 : 1;
}

export function getStartOfWeek(
	date: Date,
	weekStart: WeekStart = DEFAULT_WEEK_START
): Date {
	const result = new Date(date);
	const day = result.getDay();
	const firstDay = getFirstDayOfWeekIndex(weekStart);
	const diff = (day - firstDay + 7) % 7;
	result.setDate(result.getDate() - diff);
	result.setHours(0, 0, 0, 0);
	return result;
}

export function getEndOfWeek(
	date: Date,
	weekStart: WeekStart = DEFAULT_WEEK_START
): Date {
	return addDays(getStartOfWeek(date, weekStart), 6);
}

/**
 * Start of the calendar week `count` full weeks before the week containing `fromDate`.
 * Example (Monday start, Wed May 21): 1 → Mon May 12; 2 → Mon May 5.
 */
export function getStartOfWeeksAgo(
	count: number,
	fromDate: Date = new Date(),
	weekStart: WeekStart = DEFAULT_WEEK_START
): Date {
	const normalizedCount = Math.max(1, Math.round(count));
	const currentWeekStart = getStartOfWeek(fromDate, weekStart);
	return addDays(currentWeekStart, -normalizedCount * 7);
}

/** End of the calendar week `count` full weeks before the week containing `fromDate`. */
export function getEndOfWeeksAgo(
	count: number,
	fromDate: Date = new Date(),
	weekStart: WeekStart = DEFAULT_WEEK_START
): Date {
	return getEndOfWeek(
		getStartOfWeeksAgo(count, fromDate, weekStart),
		weekStart
	);
}

/**
 * End of the most recent complete calendar week at or before `fromDate`.
 * When `fromDate` is the last day of its week, returns that day.
 */
export function getEndOfMostRecentCompleteWeek(
	fromDate: Date = new Date(),
	weekStart: WeekStart = DEFAULT_WEEK_START
): Date {
	const today = startOfDay(fromDate);
	const weekEnd = getEndOfWeek(today, weekStart);
	if (isSameDay(today, weekEnd)) {
		return weekEnd;
	}
	return getEndOfWeeksAgo(1, today, weekStart);
}

/**
 * Span of `weekCount` complete calendar weeks ending at
 * {@link getEndOfMostRecentCompleteWeek} — used for dynamic "Last N Weeks" → Today.
 */
export function getCalendarWeekSpanBeforeToday(
	weekCount: number,
	fromDate: Date = new Date(),
	weekStart: WeekStart = DEFAULT_WEEK_START
): { startDate: Date; endDate: Date } {
	const normalizedCount = Math.max(1, Math.round(weekCount));
	const endDate = getEndOfMostRecentCompleteWeek(fromDate, weekStart);
	const startDate = addDays(
		getStartOfWeek(endDate, weekStart),
		-(normalizedCount - 1) * 7
	);
	return { startDate, endDate };
}

// =============================================================================
// Expression validation & normalization
// =============================================================================

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const RELATIVE_AGO_RE =
	/^(\d+)(days|weeks|months|quarters|years)Ago$/i;

export function isIsoDateToken(token: string): boolean {
	if (!ISO_DATE_RE.test(token)) {
		return false;
	}
	const [y, m, d] = token.split('-').map(Number);
	const date = new Date(y, m - 1, d);
	return (
		date.getFullYear() === y
		&& date.getMonth() === m - 1
		&& date.getDate() === d
	);
}

export function isRelativeAgoToken(token: string): boolean {
	return RELATIVE_AGO_RE.test(token.trim());
}

export function isSinglePresetToken(
	token: string
): token is DateRangeSinglePreset {
	return (DATE_RANGE_SINGLE_PRESETS as readonly string[]).includes(token);
}

function isValidDateRangeEndpointToken(token: string): boolean {
	const t = token.trim();
	return (
		isSinglePresetToken(t)
		|| t === 'today'
		|| t === 'yesterday'
		|| isRelativeAgoToken(t)
		|| isIsoDateToken(t)
	);
}

export function isValidDateRangeExpression(
	value: unknown
): value is DateRangeExpression {
	if (!Array.isArray(value) || value.length === 0 || value.length > 2) {
		return false;
	}
	if (!value.every((v) => typeof v === 'string' && v.trim().length > 0)) {
		return false;
	}
	if (value.length === 1) {
		return isSinglePresetToken(String(value[0]).trim());
	}
	const [start, end] = value.map((v) => String(v).trim());
	return isValidDateRangeEndpointToken(start) && isValidDateRangeEndpointToken(end);
}

export function normalizeDateRangeExpression(
	expr: DateRangeExpression
): DateRangeExpression {
	if (expr.length === 1) {
		return [String(expr[0]).trim()] as const;
	}
	return [String(expr[0]).trim(), String(expr[1]).trim()] as const;
}

/** Validates and normalizes a `dateRange` input; returns `undefined` when invalid or absent. */
export function parseDateRangeExpressionInput(
	value: unknown
): DateRangeExpression | undefined {
	if (value === null || value === undefined) {
		return undefined;
	}
	if (!isValidDateRangeExpression(value)) {
		return undefined;
	}
	return normalizeDateRangeExpression(value);
}

// =============================================================================
// Expression resolution
// =============================================================================

type RelativeUnit = 'days' | 'weeks' | 'months' | 'quarters' | 'years';

export function buildRelativeAgoToken(
	count: number,
	unit: 'day' | 'week' | 'month' | 'quarter' | 'year'
): string {
	const n = Math.max(1, Math.abs(Math.round(count)));
	const plural: RelativeUnit =
		unit === 'day'
			? 'days'
			: unit === 'week'
				? 'weeks'
				: unit === 'month'
					? 'months'
					: unit === 'quarter'
						? 'quarters'
						: 'years';
	return `${n}${plural}Ago`;
}

/** Whether resolving `expr` depends on calendar week boundaries (week start setting). */
export function expressionDependsOnWeekStart(expr: DateRangeExpression): boolean {
	if (!isValidDateRangeExpression(expr)) {
		return false;
	}
	const normalized = normalizeDateRangeExpression(expr);
	if (normalized.length === 1) {
		const token = normalized[0].trim().toLowerCase();
		return token === 'thisweek' || token === 'lastweek';
	}
	const start = normalized[0].trim().toLowerCase();
	const end = normalized[1].trim().toLowerCase();
	if (start === 'thisweek' || start === 'lastweek' || end === 'thisweek' || end === 'lastweek') {
		return true;
	}
	const startRel = parseRelativeAgoToken(normalized[0]);
	const endRel = parseRelativeAgoToken(normalized[1]);
	return startRel?.unit === 'weeks' || endRel?.unit === 'weeks';
}

/**
 * True when the expression maps to the panel **Dynamic range** form (relative offsets),
 * not a single preset or fixed ISO date pair.
 */
export function isDynamicRangeExpression(expr: DateRangeExpression): boolean {
	if (!isValidDateRangeExpression(expr) || expr.length !== 2) {
		return false;
	}
	const [startToken, endToken] = normalizeDateRangeExpression(expr);
	if (isIsoDateToken(startToken)) {
		return false;
	}
	return (
		isRelativeAgoToken(startToken)
		&& (endToken === 'today' || isRelativeAgoToken(endToken))
	);
}

export function parseRelativeAgoToken(
	token: string
): { count: number; unit: RelativeUnit } | null {
	const match = token.trim().match(/^(\d+)(days|weeks|months|quarters|years)Ago$/i);
	if (!match) {
		return null;
	}
	return {
		count: Number(match[1]),
		unit: match[2].toLowerCase() as RelativeUnit,
	};
}

/**
 * Resolves when the expression is valid and chronologically ordered (end ≥ start).
 * Returns `null` for invalid syntax, unknown tokens, or end before start — does not throw.
 */
export function tryResolveDateRangeExpression(
	expr: DateRangeExpression,
	options?: WeekStartOptions
): ResolvedDateRangeExpression | null {
	if (!isValidDateRangeExpression(expr)) {
		return null;
	}
	const normalized = normalizeDateRangeExpression(expr);
	if (normalized.length === 1) {
		try {
			const { startDate, endDate } = resolveSinglePreset(
				normalized[0] as DateRangeSinglePreset,
				options
			);
			return { startDate, endDate, expression: normalized };
		} catch {
			return null;
		}
	}
	const weeksAgoToToday = tryResolveWeeksAgoToTodayPair(normalized, options);
	if (weeksAgoToToday) {
		return weeksAgoToToday;
	}
	try {
		const startDate = resolveEndpointToStartOfDay(normalized[0], options);
		const endDate = resolveEndpointToEndOfDay(normalized[1], options);
		if (endDate.getTime() < startDate.getTime()) {
			return null;
		}
		return { startDate, endDate, expression: normalized };
	} catch {
		return null;
	}
}

export function resolveDateRangeExpression(
	expr: DateRangeExpression,
	options?: WeekStartOptions
): ResolvedDateRangeExpression {
	const result = tryResolveDateRangeExpression(expr, options);
	if (result) {
		return result;
	}
	if (!isValidDateRangeExpression(expr)) {
		throw new Error(`Invalid date range expression: ${JSON.stringify(expr)}`);
	}
	const normalized = normalizeDateRangeExpression(expr);
	throw new Error(
		`Date range end must be on or after start: ${JSON.stringify(normalized)}`
	);
}

export function toMaterialDateRangeFromExpression(
	expr: DateRangeExpression,
	options?: WeekStartOptions
): DateRange<Date> {
	const resolved = resolveDateRangeExpression(expr, options);
	return new DateRange(resolved.startDate, resolved.endDate);
}

/**
 * Builds the canonical expression for a resolved calendar range.
 */
export function expressionFromResolvedDates(
	startDate: Date,
	endDate: Date,
	options?: WeekStartOptions
): DateRangeExpression {
	const single = detectSinglePreset(startDate, endDate);
	if (single) {
		return [single];
	}
	const start = startOfDay(startDate);
	const end = startOfDay(endDate);
	const today = startOfDay(new Date());
	const relative = detectRelativeAgoPair(start, end, options);
	if (relative) {
		return relative;
	}
	if (isSameDay(end, today)) {
		return [formatDateToISO(start), 'today'];
	}
	return [formatDateToISO(start), formatDateToISO(end)];
}

export function expressionFromDynamicRelativeForm(
	startOffset: number,
	startUnit: 'day' | 'week' | 'month' | 'quarter' | 'year',
	endIsToday: boolean,
	endOffset: number,
	endUnit: 'day' | 'week' | 'month' | 'quarter' | 'year'
): DateRangeExpression {
	const startToken = buildRelativeAgoToken(startOffset, startUnit);
	if (endIsToday) {
		return [startToken, 'today'];
	}
	const endToken = buildRelativeAgoToken(endOffset, endUnit);
	return [startToken, endToken];
}

function tryResolveWeeksAgoToTodayPair(
	normalized: DateRangeExpression,
	options?: WeekStartOptions
): ResolvedDateRangeExpression | null {
	if (normalized.length !== 2 || normalized[1].trim().toLowerCase() !== 'today') {
		return null;
	}
	const startRelative = parseRelativeAgoToken(normalized[0]);
	if (startRelative?.unit !== 'weeks') {
		return null;
	}
	const weekStart = weekStartFromOptions(options);
	const { startDate, endDate } = getCalendarWeekSpanBeforeToday(
		startRelative.count,
		new Date(),
		weekStart
	);
	return {
		startDate,
		endDate: endOfDay(endDate),
		expression: normalized,
	};
}

export function expressionFromFixedAndEnd(
	startDate: Date,
	endDate: Date
): DateRangeExpression {
	const start = formatDateToISO(startDate);
	const today = startOfDay(new Date());
	if (isSameDay(endDate, today)) {
		return [start, 'today'];
	}
	return [start, formatDateToISO(endDate)];
}

// =============================================================================
// Endpoint / preset resolution
// =============================================================================

function resolveSinglePreset(
	preset: DateRangeSinglePreset,
	options?: WeekStartOptions
): {
	startDate: Date;
	endDate: Date;
} {
	const today = startOfDay(new Date());
	switch (preset) {
		case 'today':
			return { startDate: today, endDate: endOfDay(today) };
		case 'yesterday': {
			const y = addDays(today, -1);
			return { startDate: y, endDate: endOfDay(y) };
		}
		case 'last7days':
			return { startDate: addDays(today, -6), endDate: endOfDay(today) };
		case 'last30days':
			return { startDate: addDays(today, -29), endDate: endOfDay(today) };
		case 'last90days':
			return { startDate: addDays(today, -89), endDate: endOfDay(today) };
		case 'thisWeek': {
			const weekStart = weekStartFromOptions(options);
			return {
				startDate: getStartOfWeek(today, weekStart),
				endDate: endOfDay(today),
			};
		}
		case 'lastWeek': {
			const weekStart = weekStartFromOptions(options);
			const lastWeekEnd = addDays(getStartOfWeek(today, weekStart), -1);
			return {
				startDate: getStartOfWeek(lastWeekEnd, weekStart),
				endDate: endOfDay(lastWeekEnd),
			};
		}
		case 'thisMonth':
			return { startDate: getStartOfMonth(today), endDate: getEndOfMonth(today) };
		case 'lastMonth': {
			const lastMonth = addMonths(today, -1);
			return {
				startDate: getStartOfMonth(lastMonth),
				endDate: getEndOfMonth(lastMonth),
			};
		}
		case 'thisYear':
			return { startDate: getStartOfYear(today), endDate: getEndOfYear(today) };
		case 'lastYear': {
			const lastYear = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
			return {
				startDate: getStartOfYear(lastYear),
				endDate: getEndOfYear(lastYear),
			};
		}
		default:
			throw new Error(`Unknown preset: ${preset}`);
	}
}

function resolveEndpointToStartOfDay(
	token: string,
	options?: WeekStartOptions
): Date {
	const t = token.trim();
	if (isSinglePresetToken(t)) {
		return resolveSinglePreset(t, options).startDate;
	}
	if (t === 'today') {
		return startOfDay(new Date());
	}
	if (t === 'yesterday') {
		return addDays(startOfDay(new Date()), -1);
	}
	if (isIsoDateToken(t)) {
		return parseISODate(t);
	}
	const relative = parseRelativeAgoToken(t);
	if (relative) {
		return startOfDay(
			applyRelativeAgoForBoundary(relative.count, relative.unit, 'start', options)
		);
	}
	throw new Error(`Unknown date range endpoint: ${token}`);
}

function resolveEndpointToEndOfDay(
	token: string,
	options?: WeekStartOptions
): Date {
	const t = token.trim();
	if (isSinglePresetToken(t)) {
		return resolveSinglePreset(t, options).endDate;
	}
	if (t === 'today') {
		return endOfDay(new Date());
	}
	if (t === 'yesterday') {
		const y = addDays(startOfDay(new Date()), -1);
		return endOfDay(y);
	}
	if (isIsoDateToken(t)) {
		return endOfDay(parseISODate(t));
	}
	const relative = parseRelativeAgoToken(t);
	if (relative) {
		return endOfDay(
			applyRelativeAgoForBoundary(relative.count, relative.unit, 'end', options)
		);
	}
	throw new Error(`Unknown date range endpoint: ${token}`);
}

type RelativeBoundary = 'start' | 'end';

function applyRelativeAgoForBoundary(
	count: number,
	unit: RelativeUnit,
	boundary: RelativeBoundary,
	options?: WeekStartOptions
): Date {
	const today = startOfDay(new Date());

	switch (unit) {
		case 'days':
			return addDays(today, -count);
		case 'weeks': {
			const weekStart = weekStartFromOptions(options);
			return boundary === 'start'
				? getStartOfWeeksAgo(count, today, weekStart)
				: getEndOfWeeksAgo(count, today, weekStart);
		}
		case 'months': {
			const result = addMonths(today, -count);
			return new Date(result.getFullYear(), result.getMonth(), 1);
		}
		case 'quarters': {
			const result = addMonths(today, -count * 3);
			const quarterMonth = Math.floor(result.getMonth() / 3) * 3;
			return new Date(result.getFullYear(), quarterMonth, 1);
		}
		case 'years':
			return new Date(today.getFullYear() - count, 0, 1);
		default:
			throw new Error(`Unknown relative unit: ${unit}`);
	}
}

function detectSinglePreset(
	startDate: Date,
	endDate: Date
): DateRangeSinglePreset | null {
	const checks: Array<{ preset: DateRangeSinglePreset; check: () => boolean }> = [
		{
			preset: 'today',
			check: () => {
				const today = startOfDay(new Date());
				return isSameDay(startDate, today) && isSameDay(endDate, today);
			},
		},
		{
			preset: 'yesterday',
			check: () => {
				const yesterday = addDays(startOfDay(new Date()), -1);
				return isSameDay(startDate, yesterday) && isSameDay(endDate, yesterday);
			},
		},
		{
			preset: 'last7days',
			check: () => {
				const today = startOfDay(new Date());
				return (
					isSameDay(startDate, addDays(today, -6))
					&& isSameDay(endDate, today)
				);
			},
		},
		{
			preset: 'last30days',
			check: () => {
				const today = startOfDay(new Date());
				return (
					isSameDay(startDate, addDays(today, -29))
					&& isSameDay(endDate, today)
				);
			},
		},
		{
			preset: 'last90days',
			check: () => {
				const today = startOfDay(new Date());
				return (
					isSameDay(startDate, addDays(today, -89))
					&& isSameDay(endDate, today)
				);
			},
		},
		{
			preset: 'thisMonth',
			check: () => {
				const today = startOfDay(new Date());
				return (
					isSameDay(startDate, getStartOfMonth(today))
					&& isSameDay(endDate, getEndOfMonth(today))
				);
			},
		},
		{
			preset: 'lastMonth',
			check: () => {
				const today = startOfDay(new Date());
				const lastMonth = addMonths(today, -1);
				return (
					isSameDay(startDate, getStartOfMonth(lastMonth))
					&& isSameDay(endDate, getEndOfMonth(lastMonth))
				);
			},
		},
	];

	for (const { preset, check } of checks) {
		if (check()) {
			return preset;
		}
	}
	return null;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Whole calendar days from `earlier` (inclusive) to `later` (exclusive of time). */
function calendarDaysBetween(earlier: Date, later: Date): number {
	return Math.round(
		(startOfDay(later).getTime() - startOfDay(earlier).getTime()) / MS_PER_DAY
	);
}

/** Whole calendar months from the start of `earlier`'s month to `later`'s month. */
function calendarMonthsBetween(earlier: Date, later: Date): number {
	const e = startOfDay(earlier);
	const l = startOfDay(later);
	return (l.getFullYear() - e.getFullYear()) * 12 + (l.getMonth() - e.getMonth());
}

/**
 * When the range ends today, infer `[nUnitAgo, 'today']` without scanning 1..365 per unit.
 * Unit priority matches the previous brute-force order: days → weeks → months → quarters → years.
 */
function detectRelativeAgoPair(
	start: Date,
	end: Date,
	options?: WeekStartOptions
): DateRangeExpression | null {
	const today = startOfDay(new Date());
	const startDay = startOfDay(start);
	const endDay = startOfDay(end);

	// Dynamic "Last N Weeks" → Today resolves to N complete calendar weeks.
	for (let n = 1; n <= 52; n++) {
		const weekStart = weekStartFromOptions(options);
		const span = getCalendarWeekSpanBeforeToday(n, today, weekStart);
		if (isSameDay(span.startDate, startDay) && isSameDay(span.endDate, endDay)) {
			return [buildRelativeAgoToken(n, 'week'), 'today'];
		}
	}

	if (!isSameDay(endDay, today)) {
		return null;
	}

	const dayDiff = calendarDaysBetween(startDay, today);
	if (dayDiff < 1) {
		return null;
	}

	if (
		isSameDay(
			applyRelativeAgoForBoundary(dayDiff, 'days', 'start', options),
			startDay
		)
	) {
		return [buildRelativeAgoToken(dayDiff, 'day'), 'today'];
	}

	if (startDay.getDate() === 1) {
		const months = calendarMonthsBetween(startDay, today);
		if (
			months >= 1
			&& isSameDay(
				applyRelativeAgoForBoundary(months, 'months', 'start', options),
				startDay
			)
		) {
			return [buildRelativeAgoToken(months, 'month'), 'today'];
		}
	}

	if (startDay.getDate() === 1 && startDay.getMonth() % 3 === 0) {
		const months = calendarMonthsBetween(startDay, today);
		if (months >= 3 && months % 3 === 0) {
			const quarters = months / 3;
			if (
				isSameDay(
					applyRelativeAgoForBoundary(quarters, 'quarters', 'start', options),
					startDay
				)
			) {
				return [buildRelativeAgoToken(quarters, 'quarter'), 'today'];
			}
		}
	}

	if (startDay.getMonth() === 0 && startDay.getDate() === 1) {
		const years = today.getFullYear() - startDay.getFullYear();
		if (
			years >= 1
			&& isSameDay(
				applyRelativeAgoForBoundary(years, 'years', 'start', options),
				startDay
			)
		) {
			return [buildRelativeAgoToken(years, 'year'), 'today'];
		}
	}

	return null;
}

// =============================================================================
// Date math
// =============================================================================

function startOfDay(date: Date): Date {
	const result = new Date(date);
	result.setHours(0, 0, 0, 0);
	return result;
}

function endOfDay(date: Date): Date {
	const result = new Date(date);
	result.setHours(23, 59, 59, 999);
	return result;
}

function addDays(date: Date, days: number): Date {
	const result = new Date(date);
	result.setDate(result.getDate() + days);
	return result;
}

function addMonths(date: Date, months: number): Date {
	const result = new Date(date);
	result.setMonth(result.getMonth() + months);
	return result;
}

function isSameDay(date1: Date, date2: Date): boolean {
	return (
		date1.getFullYear() === date2.getFullYear()
		&& date1.getMonth() === date2.getMonth()
		&& date1.getDate() === date2.getDate()
	);
}

function getStartOfMonth(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), 1);
}

function getEndOfMonth(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function getStartOfYear(date: Date): Date {
	return new Date(date.getFullYear(), 0, 1);
}

function getEndOfYear(date: Date): Date {
	return new Date(date.getFullYear(), 11, 31);
}

// =============================================================================
// Legacy migration
// =============================================================================

/** True when value is a legacy `DynamicDateRangeConfig` object (`type` discriminator). */
export function isLegacyDynamicDateRangeConfig(
	value: unknown
): value is DynamicDateRangeConfig {
	return isValidDateRangeConfig(value);
}

/**
 * Normalizes a stored/API value to a {@link DateRangeExpression}.
 *
 * Returns `undefined` only when `raw` is `null` or `undefined` (no range stored).
 * Throws when a value is present but not a supported shape or cannot be parsed.
 */
export function coerceToDateRangeExpression(
	raw: null | undefined
): undefined;
export function coerceToDateRangeExpression(raw: unknown): DateRangeExpression;
export function coerceToDateRangeExpression(
	raw: unknown
): DateRangeExpression | undefined {
	if (raw === null || raw === undefined) {
		return undefined;
	}

	if (isValidDateRangeExpression(raw)) {
		return normalizeDateRangeExpression(raw);
	}

	if (isLegacyDynamicDateRangeConfig(raw)) {
		return legacyDynamicConfigToExpression(raw);
	}

	if (typeof raw === 'object' && !Array.isArray(raw)) {
		const o = raw as Record<string, unknown>;
		if (typeof o['preset'] === 'string') {
			return legacyPresetFieldToExpression(o['preset']);
		}
		if (
			typeof o['start'] === 'string'
			&& typeof o['end'] === 'string'
		) {
			return legacyIsoPairToExpression(o['start'], o['end']);
		}
	}

	if (Array.isArray(raw)) {
		if (raw.length === 0 || raw.length > 2) {
			throwInvalidDateRange(raw);
		}
		if (!raw.every((v) => typeof v === 'string')) {
			throwInvalidDateRange(raw);
		}
		const strings = raw.map((v) => String(v).trim());
		if (isValidDateRangeExpression(strings)) {
			return normalizeDateRangeExpression(strings as DateRangeExpression);
		}
		if (strings.length === 2) {
			return legacyIsoPairToExpression(strings[0], strings[1]);
		}
		throwInvalidDateRange(raw);
	}

	throwInvalidDateRange(raw);
}

function throwInvalidDateRange(raw: unknown): never {
	throw new Error(
		`Invalid date range expression: ${JSON.stringify(raw)}`
	);
}

function legacyDynamicConfigToExpression(
	config: DynamicDateRangeConfig
): DateRangeExpression {
	switch (config.type) {
		case DateRangeConfigType.Preset:
			return legacyPresetFieldToExpression(config.preset);
		case DateRangeConfigType.RelativeOffset:
			return relativeOffsetConfigToExpression(config);
		case DateRangeConfigType.Fixed: {
			const today = formatDateToISO(new Date());
			if (config.endDate === today) {
				return [config.startDate, 'today'];
			}
			return [config.startDate, config.endDate];
		}
		case DateRangeConfigType.RelativeReference:
		default: {
			const resolved = resolveDateRangeConfig(config);
			return expressionFromResolvedDates(
				resolved.startDate,
				resolved.endDate
			);
		}
	}
}

function legacyPresetFieldToExpression(preset: string): DateRangeExpression {
	const id = preset.trim();
	if (isSinglePresetToken(id)) {
		return [id];
	}
	const resolved = resolveDateRangeConfig({
		type: DateRangeConfigType.Preset,
		preset: id as DateRangePreset,
		label: id,
	});
	return expressionFromResolvedDates(resolved.startDate, resolved.endDate);
}

function relativeOffsetConfigToExpression(
	config: RelativeOffsetDateRangeConfig
): DateRangeExpression {
	const startToken = buildRelativeAgoToken(
		Math.abs(config.startOffset),
		config.startUnit
	);
	if (config.endOffset === 0) {
		return [startToken, 'today'];
	}
	const endToken = buildRelativeAgoToken(
		Math.abs(config.endOffset),
		config.endUnit
	);
	return [startToken, endToken];
}

function legacyIsoPairToExpression(
	start: string,
	end: string
): DateRangeExpression {
	const s = start.trim();
	const e = end.trim();
	if (isValidDateRangeExpression([s, e])) {
		return [s, e];
	}
	try {
		return expressionFromResolvedDates(parseISODate(s), parseISODate(e));
	} catch {
		throwInvalidDateRange([start, end]);
	}
}

// =============================================================================
// Presets
// =============================================================================

export interface DateRangePresetOption {
	id: string;
	label: string;
	/** Derive from {@link tryResolveDateRangeExpression} / {@link toMaterialDateRangeFromExpression}. */
	getRange: () => DateRange<Date>;
	/** Serializable expression for persistence. */
	expression: DateRangeExpression;
}

function presetOption(
	id: string,
	label: string,
	expression: DateRangeExpression
): DateRangePresetOption {
	return {
		id,
		label,
		expression,
		getRange: () => toMaterialDateRangeFromExpression(expression),
	};
}

/** Custom range placeholder: shows calendar; expression updates when user picks dates. */
function customRangeOption(): DateRangePresetOption {
	const today = new Date();
	const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
	return presetOption('custom', 'Custom range', [iso, 'today']);
}

/**
 * All available presets (comprehensive list for createPresetList).
 */
export const ALL_PRESETS: DateRangePresetOption[] = [
	presetOption('today', 'Today', ['today']),
	presetOption('yesterday', 'Yesterday', ['yesterday']),
	presetOption('last7days', 'Last 7 days', ['last7days']),
	presetOption('last30days', 'Last 30 days', ['last30days']),
	presetOption('last90days', 'Last 90 days', ['last90days']),
	presetOption('thisWeek', 'This Week', ['thisWeek']),
	presetOption('lastWeek', 'Last week', ['lastWeek']),
	presetOption('thisMonth', 'This Month', ['thisMonth']),
	presetOption('lastMonth', 'Last Month', ['lastMonth']),
	presetOption('thisYear', 'This Year', ['thisYear']),
	presetOption('lastYear', 'Last Year', ['lastYear']),
	customRangeOption(),
];

/**
 * Default presets. Order: This options first, then Last options, then Custom range.
 */
export const DEFAULT_PRESETS: DateRangePresetOption[] = [
	presetOption('yesterday', 'Yesterday', ['yesterday']),
	presetOption('last7days', 'Last 7 days', ['last7days']),
	presetOption('lastWeek', 'Last week', ['lastWeek']),
	presetOption('last30days', 'Last 30 days', ['last30days']),
	presetOption('lastMonth', 'Last Month', ['lastMonth']),
	presetOption('thisMonth', 'This Month', ['thisMonth']),
	presetOption('thisYear', 'This Year', ['thisYear']),
	presetOption('lastYear', 'Last Year', ['lastYear']),
	customRangeOption(),
];

/** Resolve preset label from expression (for display). */
export function labelForDateRangeExpression(
	expr: DateRangeExpression,
	options?: WeekStartOptions
): string {
	const id =
		expr.length === 1
			? expr[0]
			: ALL_PRESETS.find((p) => {
					const resolvedPreset = tryResolveDateRangeExpression(
						p.expression,
						options
					);
					const resolvedTarget = tryResolveDateRangeExpression(expr, options);
					if (!resolvedPreset || !resolvedTarget) {
						return false;
					}
					return (
						resolvedPreset.startDate.getTime()
							=== resolvedTarget.startDate.getTime()
						&& resolvedPreset.endDate.getTime()
							=== resolvedTarget.endDate.getTime()
					);
				})?.id;
	if (id) {
		return ALL_PRESETS.find((p) => p.id === id)?.label ?? id;
	}
	if (expr.length === 1) {
		return expr[0];
	}
	const resolved = tryResolveDateRangeExpression(expr, options);
	if (!resolved) {
		return `${String(expr[0])} – ${String(expr[1])}`;
	}
	const fmt = (d: Date) =>
		d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
	return `${fmt(resolved.startDate)} – ${fmt(resolved.endDate)}`;
}
